// Release URL patterns. These are constants in the sense that the filename shapes
// never change across versions — only the `{version}` placeholder differs.
// The *set* of listed versions is no longer hard-coded here; it's fetched at
// runtime from Azure Blob Storage (see features/downloads/api/downloadsApi.ts)
// and seeded/updated via the admin dashboard.

import { brand } from './brand';

export type BrandId = 'autopilot' | 'onbuzz';

export interface ArtifactUrls {
  windows: { filename: string; url: string; icon: string };
  mac: { filename: string; url: string; icon: string };
  linux: { filename: string; url: string; icon: string };
  npm: { package: string; installCommand: string; icon: string };
  github: { releasePageUrl: string; repoUrl: string };
}

const GITHUB_REPO = 'https://github.com/Loxia-ai/autopilot-releases';

/**
 * Artifact naming changed in v5.4.8 (CLI repo commit dc584d3): installers are
 * now built with SPACE-FREE hyphenated names ("Loxia-Autopilot-Setup-X.exe")
 * so the auto-updater's yml matches the GitHub asset byte-for-byte. Older
 * releases keep their legacy names (GitHub had dot-sanitized the space-named
 * uploads: "Loxia.Autopilot.Setup.X.exe"). The site serves BOTH eras — the
 * version list comes from Azure Blob and includes old releases — so filename
 * shapes are selected per version.
 */
const HYPHEN_NAMES_SINCE = [5, 4, 8] as const;

/** True when `version` (x.y.z) is >= the hyphen-naming cutover (5.4.8). */
export function usesHyphenNames(version: string): boolean {
  const parts = version.split('.').map((n) => parseInt(n, 10));
  for (let i = 0; i < 3; i++) {
    const v = parts[i] ?? 0;
    if (v > HYPHEN_NAMES_SINCE[i]) return true;
    if (v < HYPHEN_NAMES_SINCE[i]) return false;
  }
  return true; // exactly 5.4.8
}

/**
 * Build the 8 artifact URLs for a given version + brand. Filename and path
 * patterns are fixed per naming era; `{version}` selects the era. Keeping
 * this in one place means a CLI artifact rename only touches here, not the UI.
 */
export function buildArtifactUrls(version: string, brandId: BrandId = 'autopilot'): ArtifactUrls {
  const isOnBuzz = brandId === 'onbuzz';
  const hyphen = usesHyphenNames(version);

  const windowsFile = hyphen
    ? (isOnBuzz ? `Loxia-OnBuzz-Setup-${version}.exe` : `Loxia-Autopilot-Setup-${version}.exe`)
    : (isOnBuzz ? `Loxia.OnBuzz.Setup.${version}.exe` : `Loxia.Autopilot.Setup.${version}.exe`);
  const macFile = hyphen
    ? (isOnBuzz ? `Loxia-OnBuzz-${version}-arm64.dmg` : `Loxia-Autopilot-${version}-arm64.dmg`)
    : (isOnBuzz ? `Loxia.OnBuzz-${version}-arm64.dmg` : `Loxia.Autopilot-${version}-arm64.dmg`);
  // The .deb never carried spaces, so its name is identical in both eras.
  const linuxFile = isOnBuzz
    ? `onbuzz-${version}-amd64.deb`
    : `loxia-autopilot-${version}-amd64.deb`;

  const npmPackage = isOnBuzz ? 'onbuzz' : '@loxia-labs/loxia-autopilot-one';

  const downloadBase = `${GITHUB_REPO}/releases/download/v${version}`;

  return {
    windows: {
      filename: windowsFile,
      url: `${downloadBase}/${windowsFile}`,
      icon: '/img/logo-windows.svg',
    },
    mac: {
      filename: macFile,
      url: `${downloadBase}/${macFile}`,
      icon: '/img/logo-apple.svg',
    },
    linux: {
      filename: linuxFile,
      url: `${downloadBase}/${linuxFile}`,
      icon: '/img/logo-linux.svg',
    },
    npm: {
      package: npmPackage,
      installCommand: `npm i -g ${npmPackage}@${version}`,
      icon: '/img/logo-npm.svg',
    },
    github: {
      releasePageUrl: `${GITHUB_REPO}/releases/tag/v${version}`,
      repoUrl: GITHUB_REPO,
    },
  };
}

/**
 * @deprecated prefer `buildArtifactUrls(version, brand.id)`.
 * Kept for backward compatibility with any consumer that imports this symbol.
 * Callers who need a live version should fetch it via the downloads feature.
 */
export const RELEASE_CONFIG = {
  get version(): string {
    // Preserves old behavior: returns a static placeholder. Real versions are
    // now fetched dynamically.
    return '0.0.0';
  },
  for(version: string) {
    return buildArtifactUrls(version, (brand.id as BrandId) || 'autopilot');
  },
};

export default RELEASE_CONFIG;
